import { useEffect } from 'react';
import React from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { ScoresSearchParams } from '../../../models/overmind/search-params';
import { OrganizationLevelType, ScoreType } from '../../../enums';
import styles from './ranked-entities.module.css';
import { ScoreComparison } from '../../../enums/score-comparison';
import { transformScore, transformScores } from './ranked-entities.helpers';
import { DataPoint } from '../../../components/charts/recharts.models';
import { IRankedEntitiesWidgetProps } from './ranked-entities.models';
import { RankedPerformanceChart } from '../../../components/charts/ranked-performance-chart/ranked-performance-chart';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../analytics-filters/widget-card';

export default function RankedEntitiesWidget({ organizationLevelType, entitySlugs, title, subtitle, rankingLabel, byScoreType = false }: IRankedEntitiesWidgetProps): JSX.Element {
  const { companyVariables, dateRange } = useAppState();
  const actions = useAppActions();

  const [highestScores, setHighestScores] = React.useState<DataPoint[]>([]);
  const [lowestScores, setLowestScores] = React.useState<DataPoint[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  useEffect(() => {
    async function getDataByScoreType() {
      const searchParams: ScoresSearchParams = {
        search: undefined,
        sortAscending: undefined,
        page: 1,
        pageSize: 5,
        startDate: undefined,
        endDate: undefined,
        organizationLevel: organizationLevelType,
        companySlug: companyVariables.slug,
        departmentSlugs: undefined,
        teamSlugs: undefined,
        learnerSlug: undefined,
        scoreType: ScoreType.Role,
        scoreTypeSlugs: undefined,
        scoreComparison: ScoreComparison.RoleExpectedProficiency,
      };
      if (organizationLevelType !== undefined) {
        if (organizationLevelType === OrganizationLevelType.Department) searchParams.departmentSlugs = entitySlugs;
        if (organizationLevelType === OrganizationLevelType.Team) searchParams.teamSlugs = entitySlugs;
        if (organizationLevelType === OrganizationLevelType.Learner) searchParams.learnerSlug = entitySlugs[0];
      }

      const rankedResults = await actions.analyticsGetRankedScoresByScoreType(searchParams);

      setHighestScores(transformScore(rankedResults?.highest ?? [], companyVariables));
      setLowestScores(transformScore(rankedResults?.lowest ?? [], companyVariables));
    }

    async function getDataByOrganizationLevel() {
      const startDate = dateRange?.from?.toJSON();
      const endDate = dateRange?.to?.toJSON();
      const searchParams: ScoresSearchParams = {
        search: undefined,
        sortAscending: undefined,
        page: 1,
        pageSize: 5,
        startDate,
        endDate,
        organizationLevel: organizationLevelType,
        companySlug: companyVariables.slug,
        departmentSlugs: undefined,
        teamSlugs: undefined,
        learnerSlug: undefined,
        scoreType: ScoreType.SkillType,
        scoreTypeSlugs: ['Overall'],
        scoreComparison: undefined,
      };
      if (organizationLevelType !== undefined) {
        if (organizationLevelType === OrganizationLevelType.Department) searchParams.departmentSlugs = entitySlugs;
        if (organizationLevelType === OrganizationLevelType.Team) searchParams.teamSlugs = entitySlugs;
        if (organizationLevelType === OrganizationLevelType.Learner) searchParams.learnerSlug = entitySlugs[0];
      }

      const rankedResults = await actions.analyticsGetRankedScoresByOrganizationLevel(searchParams);

      setHighestScores(transformScores(rankedResults?.highest ?? [], companyVariables));
      setLowestScores(transformScores(rankedResults?.lowest ?? [], companyVariables));
    }

    async function getData() {
      if (organizationLevelType === undefined) return;

      setIsLoading(true);

      if (byScoreType) {
        await getDataByScoreType();
      } else {
        await getDataByOrganizationLevel();
      }

      setIsLoading(false);
    }

    getData();
  }, [companyVariables.slug, dateRange, organizationLevelType, entitySlugs]);

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%">
      <WidgetCard title={title ? title : 'Highest scoring users'} subtitle={subtitle ? subtitle : 'An overview of user performance, highlighting users that have achieved the highest scores and those with opportunities for improvement.'} showSkillLevelHelp>
        <div className={styles.widgetContainer}>
          <RankedPerformanceChart title={`Highest rated ${rankingLabel ?? ''}`} dataPoints={highestScores} fill="baby-blue" />
          <RankedPerformanceChart title={`Lowest rated ${rankingLabel ?? ''}`} dataPoints={lowestScores} fill="orange" />
        </div>
      </WidgetCard>
    </SkeletonOverlay>
  );
}
